import { useEffect } from 'react';

import { useSearchParams } from 'next/navigation';

import { getIsUA } from '@domain/analytics/getIsUa';
import { getReferringDomain } from '@domain/analytics/getReferringDomain';
import { trackEvent } from '@domain/analytics/trackEvent';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { useCMSSlug } from '@domain/routes/useCMSSlug';
import { logger } from '@lib/logger/logger';
import { QueryStringKey } from '@lib/url/queryUtils';

interface PageViewProps {
  page: string;
  pageTitle: string;
  pageId?: string;
  pageSubject?: string;
  previousPage: string | null;
}

export function useAmplitudePageView({
  page,
  pageId,
  pageSubject,
  previousPage,
}: PageViewProps): void {
  const searchParams = useSearchParams();
  const cmsSlug = useCMSSlug();
  const clientHasRendered = useClientHasRendered();

  useEffect(() => {
    if (clientHasRendered) {
      logger.debug({ page, pageId }, `Page viewed`);
      const isFirstPage = previousPage === null;
      trackEvent({
        name: 'mykiss_page_viewed',
        properties: {
          page,
          ...(previousPage ? { prev_page: previousPage } : {}),
          is_first_page_viewed: isFirstPage,
          page_id: pageId,
          page_subject: pageSubject,
          is_shop: false, // todo remove
          page_slug: cmsSlug,
          user_agent: navigator.userAgent,
        },
      });

      if (isFirstPage) {
        trackEvent({
          name: 'mykiss_first_page_viewed',
          properties: {
            page,
            referrer: document.referrer,
            rootReferrer: document.referrer.length
              ? getReferringDomain(document.referrer)
              : undefined,
            isUA: getIsUA(searchParams),
            is_shop: false, // todo remove
            campaign: searchParams.get(QueryStringKey.Campaign) ?? undefined,
            page_slug: cmsSlug,
            user_agent: navigator.userAgent,
          },
        });
      }
    }
    // We explicitly only want this hook to rerun if the page or pageId change (indicating a change
    // in the current route).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageId, clientHasRendered]);
}
